import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { apiClient } from "../../services/api/apiClient";
import {Cog6ToothIcon } from "@heroicons/react/24/outline";
import PipeIcon from "../../assets/PipeIcon";
import DuctIcon from "../../assets/DuctIcon";
import logo from "../../assets/helmetwhite1024.png";
import ProjectSelection from "../projects/ProjectSelection"; // Import the ProjectSelection component
import AddProjectModal from "../projects/AddProjectModal"; // Import the AddProjectModal component
import SignOutButton from "./SignOutButton";
import DuctSchedule from "../../pages/DuctSchedule";

const navigation = [
  {
    name: "Piping Schedule",
    href: "/piping-schedule",
    icon: PipeIcon,
  },
  { name: "Duct Schedule", href: "/duct-schedule", icon: DuctIcon },
  { name: "Settings", href: "/settings", icon: Cog6ToothIcon },
];

const teams = [
  // { id: 1, name: "Heroicons", href: "/team/heroicons", initial: "H" },
  // { id: 2, name: "Tailwind Labs", href: "/team/tailwind-labs", initial: "T" },
  // { id: 3, name: "Workcation", href: "/team/workcation", initial: "W" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getInitials(name) {
  if (!name) return "U";
  const names = name.split(" ").filter(Boolean);
  if (names.length === 1) return names[0][0].toUpperCase();
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
}

export default function SideNav() {
  const [currentMenuItem, setCurrentMenuItem] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAddProjectOpen, setIsAddProjectOpen] = useState(false); // Modal state
  const [projectData, setProjectData] = useState(null);
  const location = useLocation();
  const selectedProjectId = useSelector(
    (state) => state.project.selectedProjectId
  );
  const projectRefreshTimestamp = useSelector(
    (state) => state.project.projectRefreshTimestamp
  );
  const { accounts } = useMsal();
  const account = accounts && accounts[0];

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!selectedProjectId) {
        console.log("No project selected, skipping fetch");
        return;
      }

      try {
        console.log(
          "fetching project data inside the sidebar for project:",
          selectedProjectId
        );

        const response = await apiClient.get(`/projects/${selectedProjectId}`);
        setProjectData(response.data);
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    };
    fetchProjectData();
  }, [selectedProjectId, projectRefreshTimestamp]);

  useEffect(() => {
    const currentPath = location.pathname;
    const activeMenuItem = navigation.find((item) => item.href === currentPath);
    setCurrentMenuItem(activeMenuItem ? activeMenuItem.name : "");
  }, [location.pathname]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const openAddProject = () => {
    setIsAddProjectOpen(true); // Open the modal
  };

  const closeAddProject = () => {
    setIsAddProjectOpen(false); // Close the modal
  };

  return (
    <div className="flex h-screen">
      {/* Main Sidebar */}
      <div className="fixed top-0 left-0 h-screen w-64 bg-gray-900 flex flex-col px-6">
        <div className="flex h-16 shrink-0 items-center justify-center mb-6 mt-6 px-12">
          <img className="h-14 w-auto" src={logo} alt="Your Company" />
        </div>
        <div className="min-w-0 text-center">
          <h2 className="text-2xl mb-2 font-bold leading-7 text-white sm:truncate sm:text-3xl sm:tracking-tight">
            {projectData?.job_number}
          </h2>
        </div>
        <div className="min-w-0 text-center">
          <h2 className="text-sm font-medium text-gray-400 hover:text-gray-200">
            {projectData?.job_name}
          </h2>
        </div>
        <button
          type="button"
          className="rounded-md bg-indigo-600 mb-6 mt-2 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          onClick={toggleSidebar}
        >
          {selectedProjectId ? "Change Project" : "Select Project"}
        </button>
        <nav className="flex flex-1 flex-col">
          <ul className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul className="-mx-2 space-y-1">
                {navigation.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className={classNames(
                        currentMenuItem === item.name
                          ? "bg-gray-800 text-white"
                          : "text-gray-400 hover:bg-gray-800 hover:text-white",
                        "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6"
                      )}
                      onClick={() => setCurrentMenuItem(item.name)}
                    >
                      <item.icon
                        className="h-6 w-6 shrink-0"
                        aria-hidden="true"
                      />
                      {item.name}
                      {item.count ? (
                        <span
                          className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-gray-900 px-2.5 py-0.5 text-center text-xs font-medium leading-5 text-white ring-1 ring-inset ring-gray-700"
                          aria-hidden="true"
                        >
                          {item.count}
                        </span>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <div className="text-xs font-semibold leading-6 text-gray-400">
                {/* This is where you will put a section divider for the next section */}
              </div>
              <ul className="-mx-2 mt-2 space-y-1">
                {teams.map((team) => (
                  <li key={team.name}>
                    <Link
                      to={team.href}
                      className={classNames(
                        currentMenuItem === team.name
                          ? "bg-gray-800 text-white"
                          : "text-gray-400 hover:bg-gray-800 hover:text-white",
                        "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6"
                      )}
                      onClick={() => setCurrentMenuItem(team.name)}
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-700 bg-gray-800 text-[0.625rem] font-medium text-gray-400 group-hover:text-white">
                        {team.initial}
                      </span>
                      <span className="truncate">{team.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            <li className="-mx-6 mt-auto">
              <div className="flex items-center gap-x-4 px-6 py-3 text-sm font-semibold leading-6 text-white">
                {account?.imageUrl ? (
                  <img
                    className="h-8 w-8 rounded-full bg-gray-800"
                    src={account.imageUrl}
                    alt="User profile"
                  />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-800 text-white font-bold text-lg">
                    {getInitials(account?.name)}
                  </span>
                )}
                <span className="sr-only">Your profile</span>
                <span aria-hidden="true">{account?.name || "User"}</span>
              </div>
              <div className="px-6 pb-3">
                <SignOutButton />
              </div>
            </li>
          </ul>
        </nav>
      </div>

      {/* Sliding Sidebar */}
      <div className="ml-64 flex-1 h-screen overflow-y-auto">
        {isSidebarOpen && (
          <div
            className={classNames(
              isSidebarOpen ? "translate-x-0" : "-translate-x-full",
              "fixed inset-y-0 left-0 z-40 w-[400px] bg-gray-100 transition-transform duration-300 ease-in-out shadow-xl"
            )}
          >
            <button
              className="text-black text-sm mt-4 ml-4"
              onClick={toggleSidebar}
            >
              Close
            </button>
            <ProjectSelection
              onOpenAddProject={openAddProject}
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={toggleSidebar}
            />
            {/* Trigger modal */}
          </div>
        )}
      </div>

      {/* Add Project Modal now uses Portal and renders at document.body */}
      <AddProjectModal
        isOpen={isAddProjectOpen}
        onClose={closeAddProject}
        onSubmit={(newProject) => console.log(newProject)}
      />

      {/* Overlay for when the sidebar is open */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-30"
          onClick={toggleSidebar}
        ></div>
      )}
    </div>
  );
}
